/**
 * <covaso-exit-popup> — email capture popup.
 *
 * Desktop: opens on exit intent (cursor leaves through the top edge).
 * Mobile: configurable — off, time delay, or fast scroll-up after
 * scrolling deep (there is no real exit intent on touch devices).
 * Frequency-capped via localStorage. Opens immediately in success state
 * (after the customer form round-trip) and when selected in the editor.
 */
class CovasoExitPopup extends HTMLElement {
  static STORAGE_KEY = 'covaso_exit_shown';

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-covaso-ep-close]')) this.dialog.close();
      else if (e.target === this.dialog) this.dialog.close();
    });

    if (Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        if (e.target.contains(this)) this.dialog.showModal();
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.target.contains(this)) this.dialog.close();
      });
      return;
    }

    if (this.dataset.posted === 'true') {
      // Returning from a successful submit — show the success state once.
      this.openNow();
      return;
    }

    if (this.isSuppressed()) return;

    const armDelay = (parseFloat(this.dataset.armDelay) || 5) * 1000;
    setTimeout(() => this.arm(), armDelay);
  }

  isSuppressed() {
    try {
      const shownAt = parseInt(localStorage.getItem(CovasoExitPopup.STORAGE_KEY) || '0', 10);
      const days = parseFloat(this.dataset.frequencyDays) || 7;
      return shownAt > 0 && Date.now() - shownAt < days * 86400 * 1000;
    } catch (e) {
      return false;
    }
  }

  arm() {
    this.onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) this.open();
    };
    document.addEventListener('mouseout', this.onMouseOut);

    const mobileTrigger = this.dataset.mobileTrigger;
    if (mobileTrigger === 'time') {
      const delay = (parseFloat(this.dataset.mobileDelay) || 20) * 1000;
      this.mobileTimer = setTimeout(() => this.open(), delay);
    } else if (mobileTrigger === 'scrollup') {
      let deepest = 0;
      this.onScroll = () => {
        const y = window.scrollY;
        if (y > deepest) deepest = y;
        if (deepest > 600 && y < deepest - 400) this.open();
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }

  open() {
    if (this.dialog.open || this.isSuppressed()) return;
    this.openNow();
    try {
      localStorage.setItem(CovasoExitPopup.STORAGE_KEY, String(Date.now()));
    } catch (e) {
      /* ignore */
    }
    if (this.onMouseOut) document.removeEventListener('mouseout', this.onMouseOut);
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    clearTimeout(this.mobileTimer);
  }

  openNow() {
    if (!this.dialog.open) this.dialog.showModal();
  }
}

if (!customElements.get('covaso-exit-popup')) {
  customElements.define('covaso-exit-popup', CovasoExitPopup);
}
